/**
 * New doc (slice 2d spec §10.5; boards 51, 51c): a dialog, a bottom sheet on
 * a phone, asking only for the title. Create and edit opens the doc page's
 * editor on an empty body (`/docs/new`); nothing is made until its first
 * Save, so a Discard leaves nothing behind. From Task detail it links the task.
 */
import { Button } from "@crew/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@crew/ui/components/drawer";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { newDocHref, TITLE_MAX } from "./doc-view";

function NewDocForm({
  mobile,
  onCreate,
  onCancel,
}: {
  mobile: boolean;
  onCreate: (title: string) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const ready = title.trim().length > 0;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onCreate(title.trim());
  };
  const Footer = mobile ? "div" : DialogFooter;
  const cancel = (
    <Button type="button" variant="ghost" onClick={onCancel}>
      Cancel
    </Button>
  );
  return (
    <form onSubmit={submit} className="flex flex-col gap-5" data-new-doc>
      <div className={mobile ? "flex flex-col gap-2 px-4" : "flex flex-col gap-5"}>
        <Field>
          <FieldLabel htmlFor="new-doc-title" className={mobile ? "sr-only" : undefined}>
            Title
          </FieldLabel>
          <Input
            id="new-doc-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX}
            placeholder="House rules for the sitter"
            autoFocus
          />
          <FieldDescription>
            {mobile
              ? "You'll write it next, in the editor. The crew can read and change it too."
              : "The crew can read and change it too."}
          </FieldDescription>
        </Field>
      </div>
      <Footer className={mobile ? "flex items-center justify-end gap-2 px-4 pb-4" : undefined}>
        {cancel}
        <Button type="submit" disabled={!ready}>
          Create and edit
        </Button>
      </Footer>
    </form>
  );
}

export function NewDocWindow({
  open,
  onOpenChange,
  taskId,
  areaId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** From Task detail: the doc is linked to this task at its first Save. */
  taskId?: string;
  /** From Docs filtered to an Area (slice 6d spec §4.2): it's filed there. */
  areaId?: string;
}) {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const close = () => onOpenChange(false);
  const form = open ? (
    <NewDocForm
      mobile={mobile}
      onCancel={close}
      onCreate={(title) => {
        close();
        void navigate(newDocHref(title, taskId, areaId));
      }}
    />
  ) : null;
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
        <DrawerContent showCloseButton className="pb-[env(safe-area-inset-bottom)]">
          <DrawerHeader className="pb-4">
            <DrawerTitle>New doc</DrawerTitle>
          </DrawerHeader>
          {form}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>New doc</DialogTitle>
          <DialogDescription>
            Give it a title. You'll write it next, in the editor.
          </DialogDescription>
        </DialogHeader>
        {form}
      </DialogContent>
    </Dialog>
  );
}
